import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { Pagination } from '@/components/shared/pagination';
import {
  messageListCriteriaFromForm,
  messageListCriteriaToForm,
  MessageListCriteriaFields,
  type MessageListCriteriaForm,
  validateMessageListCriteriaForm,
} from '@/components/messaging/message-list-criteria-fields';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { contactReadiness, memberHasEmail, memberHasPhone } from '@/lib/messaging-contact';

interface ListCriteria extends Record<string, unknown> {}

interface MessageListDetail {
  id: string;
  name: string;
  description: string | null;
  kind: 'fixed' | 'dynamic';
  criteria: ListCriteria | null;
  memberCount: number;
}

interface ListMemberRow {
  memberId: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  memberType: 'first_timer' | 'visitor' | 'member';
}

interface MemberSearchRow {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  memberType: 'first_timer' | 'visitor' | 'member';
}

interface EditListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listId: string | null;
}

export const EditListDialog = ({ open, onOpenChange, listId }: EditListDialogProps) => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const { t: tPeople } = useTranslation('people');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [criteriaForm, setCriteriaForm] = useState<MessageListCriteriaForm>(
    messageListCriteriaToForm(null),
  );
  const [memberQuery, setMemberQuery] = useState('');
  const [memberPage, setMemberPage] = useState(1);
  const [memberPageSize, setMemberPageSize] = useState(25);

  const detail = useQuery({
    queryKey: ['messaging', 'list', listId],
    queryFn: () => api.get<MessageListDetail>(`/messaging/lists/${listId}`),
    enabled: open && Boolean(listId),
  });

  const kind = detail.data?.kind ?? 'fixed';

  useEffect(() => {
    if (!open || !detail.data) return;
    setName(detail.data.name);
    setDescription(detail.data.description ?? '');
    setCriteriaForm(messageListCriteriaToForm(detail.data.criteria));
    setMemberQuery('');
    setMemberPage(1);
  }, [open, detail.data]);

  const departments = useQuery({
    queryKey: ['messaging', 'departments'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>('/people/departments?pageSize=100'),
    enabled: open && kind === 'dynamic',
  });

  const groups = useQuery({
    queryKey: ['messaging', 'groups'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>('/groups?pageSize=100'),
    enabled: open && kind === 'dynamic',
  });

  const listMembers = useQuery({
    queryKey: ['messaging', 'list-members', listId, memberPage, memberPageSize],
    queryFn: () =>
      api.get<{
        data: ListMemberRow[];
        page: {
          page: number;
          pageSize: number;
          totalItems: number;
          totalPages: number;
        };
      }>(`/messaging/lists/${listId}/members?page=${memberPage}&pageSize=${memberPageSize}`),
    enabled: open && Boolean(listId),
  });

  const memberSearch = useQuery({
    queryKey: ['messaging', 'list-member-search-edit', memberQuery],
    queryFn: () =>
      api.get<{ data: MemberSearchRow[] }>(
        `/people/members?pageSize=50&status=active${
          memberQuery ? `&q=${encodeURIComponent(memberQuery)}` : ''
        }`,
      ),
    enabled: open && kind === 'fixed',
  });

  const criteriaValidation = validateMessageListCriteriaForm(criteriaForm);
  const criteriaInvalid = criteriaValidation.invalid;

  const buildCriteria = (): ListCriteria => messageListCriteriaFromForm(criteriaForm);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['messaging', 'lists'] });
    void queryClient.invalidateQueries({ queryKey: ['messaging', 'list', listId] });
    void queryClient.invalidateQueries({ queryKey: ['messaging', 'list-members', listId] });
  };

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/messaging/lists/${listId}`, {
        name: name.trim(),
        description: description.trim() || null,
        ...(kind === 'dynamic' ? { criteria: buildCriteria() } : {}),
      }),
    onSuccess: () => {
      toast.success(t('lists.saved'));
      invalidate();
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const addMember = useMutation({
    mutationFn: (memberId: string) =>
      api.post(`/messaging/lists/${listId}/members`, { memberIds: [memberId] }),
    onSuccess: () => {
      toast.success(t('lists.memberAdded'));
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const removeMember = useMutation({
    mutationFn: (memberId: string) =>
      api.delete(`/messaging/lists/${listId}/members/${memberId}`),
    onSuccess: () => {
      toast.success(t('lists.memberRemoved'));
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const selectedIds = new Set(listMembers.data?.data.map((row) => row.memberId) ?? []);
  const memberOptions =
    memberSearch.data?.data
      .filter((row) => !selectedIds.has(row.id))
      .map((row) => ({
        id: row.id,
        label: `${row.firstName} ${row.lastName}`.trim(),
      })) ?? [];

  const listContact = contactReadiness(listMembers.data?.data ?? []);

  const memberColumns: DataTableColumn<ListMemberRow>[] = [
    {
      id: 'name',
      header: t('lists.selectedColumns.name'),
      cell: (row) => (
        <MemberNameLink
          memberId={row.memberId}
          name={`${row.firstName} ${row.lastName}`.trim()}
        />
      ),
    },
    {
      id: 'type',
      header: t('lists.selectedColumns.type'),
      cell: (row) => tPeople(`members.type.${row.memberType}`),
      hideBelowMd: true,
    },
    {
      id: 'phone',
      header: t('lists.selectedColumns.phone'),
      cell: (row) =>
        memberHasPhone(row) ? row.phone : <span className="text-fg-secondary">—</span>,
      hideBelowMd: true,
    },
    {
      id: 'email',
      header: t('lists.selectedColumns.email'),
      cell: (row) =>
        memberHasEmail(row) ? row.email : <span className="text-fg-secondary">—</span>,
      hideBelowMd: true,
    },
  ];

  const canSave =
    name.trim().length > 0 &&
    !save.isPending &&
    Boolean(detail.data) &&
    (kind !== 'dynamic' || !criteriaInvalid);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t('lists.edit')}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[75vh] pr-2">
          {!detail.data && detail.isLoading ? (
            <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="edit-list-name">{t('lists.name')}</Label>
                  <Input
                    id="edit-list-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div>
                  <Label>{t('lists.kind')}</Label>
                  <p className="mt-2 text-body-sm text-fg-secondary">
                    {kind === 'fixed' ? t('lists.kindFixed') : t('lists.kindDynamic')}
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="edit-list-description">{t('lists.description')}</Label>
                <Textarea
                  id="edit-list-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </div>

              {kind === 'dynamic' ? (
                <MessageListCriteriaFields
                  value={criteriaForm}
                  onChange={setCriteriaForm}
                  departments={departments.data?.data ?? []}
                  groups={groups.data?.data ?? []}
                />
              ) : (
                <div>
                  <Label>{t('lists.addMembers')}</Label>
                  <EntityCombobox
                    value={null}
                    onChange={(id) => {
                      if (id) addMember.mutate(id);
                    }}
                    options={memberOptions}
                    onQueryChange={setMemberQuery}
                    loading={memberSearch.isPending}
                    placeholder={t('lists.searchMembers')}
                    popoverPortal="dialog"
                    clearLabel={tCommon('action.clearSelection')}
                  />
                </div>
              )}

              <div>
                <Label>
                  {kind === 'dynamic' ? t('lists.matchingPeople') : t('lists.selectedMembers')}
                </Label>
                <div className="mt-2">
                  <DataTable
                    rows={listMembers.data?.data ?? []}
                    columns={memberColumns}
                    rowKey={(row) => row.memberId}
                    loading={listMembers.isLoading}
                    empty={
                      <p className="text-body-sm text-fg-secondary">
                        {kind === 'dynamic'
                          ? t('lists.noMatches')
                          : t('lists.noMembersSelected')}
                      </p>
                    }
                    actionsHeader={kind === 'fixed' ? tCommon('table.actions') : undefined}
                    rowActions={
                      kind === 'fixed'
                        ? (row) => (
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={removeMember.isPending}
                              onClick={() => removeMember.mutate(row.memberId)}
                            >
                              {tCommon('action.remove')}
                            </Button>
                          )
                        : undefined
                    }
                  />
                </div>
                {listMembers.data?.page && listMembers.data.page.totalPages > 1 ? (
                  <Pagination
                    className="mt-4"
                    page={listMembers.data.page.page}
                    pageSize={listMembers.data.page.pageSize}
                    totalItems={listMembers.data.page.totalItems}
                    totalPages={listMembers.data.page.totalPages}
                    onPageChange={setMemberPage}
                    onPageSizeChange={(next) => {
                      setMemberPageSize(next);
                      setMemberPage(1);
                    }}
                    labels={{
                      showing: ({ from, to, total }) =>
                        tCommon('pagination.showing', { from, to, total }),
                      perPage: tCommon('pagination.perPage'),
                      first: tCommon('pagination.first'),
                      previous: tCommon('pagination.previous'),
                      next: tCommon('pagination.next'),
                      last: tCommon('pagination.last'),
                    }}
                  />
                ) : null}
                <p className="mt-1 text-caption text-fg-secondary">
                  {t('lists.memberCount', {
                    count:
                      listMembers.data?.page.totalItems ??
                      detail.data?.memberCount ??
                      0,
                  })}
                </p>
                {listContact.total > 0 ? (
                  <p className="mt-1 text-caption text-fg-secondary">
                    {t('lists.contactReadiness', {
                      withPhone: listContact.withPhone,
                    })}
                  </p>
                ) : null}
                {listContact.missingPhone > 0 ? (
                  <p className="mt-1 text-body-sm text-warning" role="status">
                    {t('lists.missingPhoneWarning', { count: listContact.missingPhone })}
                  </p>
                ) : null}
                {kind === 'dynamic' ? (
                  <p className="mt-1 text-caption text-fg-secondary">{t('lists.dynamicMembersHelp')}</p>
                ) : null}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => onOpenChange(false)}>
                  {tCommon('action.cancel')}
                </Button>
                <Button disabled={!canSave} onClick={() => save.mutate()}>
                  {tCommon('action.saveChanges')}
                </Button>
              </div>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
